// src/app/core/services/auth.service.ts
import { Injectable } from '@angular/core';
import { Auth, signInWithEmailAndPassword, signOut, User, user as firebaseUser, getIdToken } from '@angular/fire/auth';
import { BehaviorSubject, Observable, of, combineLatest } from 'rxjs';
import { Router } from '@angular/router';
import { Firestore, doc, docData } from '@angular/fire/firestore';
import { switchMap, map, shareReplay } from 'rxjs/operators';
import { AppUser } from '../models/user';

function normalizeRoles(input: unknown): string[] | Record<string, boolean> {
  if (Array.isArray(input)) return input;
  if (input && typeof input === 'object') return input as Record<string, boolean>;
  return []; // sin roles
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private userLoggedIn = new BehaviorSubject<boolean>(false);
  isLoggedIn$ = this.userLoggedIn.asObservable();

  /** User de Firebase (tal cual) */
  user$: Observable<User | null>;

  /** AppUser enriquecido con roles desde Firestore */
  appUser$: Observable<AppUser | null>;

  constructor(
    private auth: Auth,
    private router: Router,
    private firestore: Firestore
  ) {
    this.user$ = firebaseUser(this.auth);

    this.appUser$ = this.user$.pipe(
      switchMap((u) => {
        if (!u) return of(null);
        const ref = doc(this.firestore, `users/${u.uid}`);
        // docData puede emitir {} si no existe; lo tipamos como any y controlamos abajo
        return combineLatest([
          of(u),
          docData(ref) as Observable<any | undefined>
        ]).pipe(
          map(([fu, snap]) => {
            const roles = normalizeRoles(snap?.roles);
            const appUser: AppUser = {
              uid: fu.uid,
              email: fu.email,
              nombre: snap?.nombre || '',
              apellidoPaterno: snap?.apellidoPaterno || '',
              apellidoMaterno: snap?.apellidoMaterno || '',
              estado: snap?.estado !== false,
              roles
            };
            return appUser;
          })
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  login(email: string, password: string): Promise<User> {
    return signInWithEmailAndPassword(this.auth, email, password).then((cred) => {
      this.userLoggedIn.next(true);
      return cred.user;
    });
  }

  logout() {
    return signOut(this.auth).then(() => {
      this.userLoggedIn.next(false);
      this.router.navigate(['/login']);
    });
  }

  async getIdToken(): Promise<string | null> {
    const user = this.auth.currentUser;
    if (!user) return null;
    try {
      return await getIdToken(user, true);
    } catch (e) {
      console.error('Error al obtener el token:', e);
      return null;
    }
  }

  /** Leer el doc completo de Firestore (si lo necesitas en otros lugares) */
  getUserData(uid: string): Observable<AppUser | null> {
    console.log(uid);
    
    const userDoc = doc(this.firestore, `users/${uid}`);
    return docData(userDoc) as Observable<AppUser | null>;
  }
}
